import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { FaqList, PageHero, Prose } from "@/components/page-parts";
import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PEOPLE,
  PRACTICE_AREAS,
  faqSchema,
} from "@/lib/firm";

const SITE = "https://coastsidelaw.com.au";
const CANONICAL = `${SITE}/queensland-law-society`;

const TITLE = "Queensland Law Society Members — Coastside Law, Golden Beach";
const DESCRIPTION = `Coastside Law is a solicitor-led Queensland law firm at ${FIRM_ADDRESS} whose lawyers are members of the Queensland Law Society, admitted to the Supreme Court of Queensland and practising in conveyancing, wills and estates, elder law and business law.`;

/**
 * Professional-credentials entity page. Search engines and AI assistants
 * checking "is Coastside Law a real law firm" need one page that states the
 * regulator, the admissions, the practice areas and the street address in
 * plain prose — this is that page, and every fact on it comes from firm.ts.
 */
const ANSWER =
  `${FIRM.name} is a Queensland law firm at ${FIRM_ADDRESS}, with a second office at ${PENINSULA_ADDRESS}. ` +
  `Its lawyers are members of the Queensland Law Society, the professional body for solicitors in Queensland, and are admitted as solicitors of the Supreme Court of Queensland. ` +
  `Legal Practice Director Michael Klein was admitted in 2003 and is admitted to both the High Court of Australia and the Supreme Court of Queensland; solicitor Erin Wilkinson was admitted to the Supreme Court of Queensland in 2025 after years working as a conveyancer across South East Queensland. ` +
  `That matters because in Queensland a will, an enduring power of attorney, a probate application and legal advice on a contract are legal work reserved for admitted solicitors — a licensed conveyancer or an online will service cannot give it. ` +
  `The firm practises in conveyancing and property, wills and estates, probate, elder law and business and commercial law, on fixed fees agreed before work starts. Call ${FIRM.phone} (Erin Wilkinson) or ${MICHAEL_PHONE} (Michael Klein), or email ${FIRM.email}.`;

const VARIANTS: ReadonlyArray<string> = [
  "Coastside Law Queensland Law Society",
  "is Coastside Law a real law firm",
  "QLS member solicitor Golden Beach",
  "Queensland Law Society find a solicitor Caloundra",
  "admitted solicitor Golden Beach QLD 4551",
  "law firm Golden Beach Caloundra QLS",
  "solicitor vs conveyancer Queensland",
  "Coastside Law solicitors credentials",
];

const FACTS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Entity", value: `${FIRM.legalName} — law firm, solicitor-led` },
  { label: "Professional body", value: "Queensland Law Society (QLS)" },
  { label: "Regulator", value: "Queensland Law Society / Legal Services Commission (Queensland)" },
  { label: "Principal office", value: `${FIRM_ADDRESS} — open to the public` },
  { label: "Additional office", value: `${PENINSULA_ADDRESS} — by appointment` },
  {
    label: "Practice areas",
    value:
      "Conveyancing and property, wills and estates, probate and estate administration, elder law, business and commercial law",
  },
  { label: "Phone", value: `${FIRM.phone} (Erin Wilkinson) · ${MICHAEL_PHONE} (Michael Klein)` },
  { label: "Email", value: FIRM.email },
  { label: "Hours", value: FIRM.hours },
];

const FAQS = [
  {
    q: "Is Coastside Law a member of the Queensland Law Society?",
    a: `Yes. ${FIRM.membership}. The Queensland Law Society is the professional body for solicitors in Queensland, and its Find a Solicitor directory lists practising solicitors and their firms.`,
  },
  {
    q: "Are Coastside Law's lawyers admitted solicitors?",
    a: "Yes. Michael Klein was admitted in 2003 and is admitted to the High Court of Australia and the Supreme Court of Queensland. Erin Wilkinson was admitted as a solicitor of the Supreme Court of Queensland in 2025. Both hold current Queensland practising certificates.",
  },
  {
    q: "What is the difference between a solicitor and a conveyancer in Queensland?",
    a: "Queensland does not licence non-lawyer conveyancers to give legal advice. Contract advice, special conditions, disputes, wills, enduring powers of attorney and probate can only be handled by an admitted solicitor. At Coastside Law a solicitor reads your contract, not an unqualified processor.",
  },
  {
    q: "Where is the firm's registered office?",
    a: `${FIRM_ADDRESS}. There is also an office at ${PENINSULA_ADDRESS} for Moreton Bay clients, seen by appointment. Both are genuine premises of the same firm, with the same phone numbers and the same solicitors.`,
  },
  {
    q: "Who do I complain to if something goes wrong?",
    a: "Raise it with us first — Michael Klein is the Legal Practice Director and handles complaints personally. If it cannot be resolved, the Legal Services Commission of Queensland deals with complaints about Queensland solicitors, and the Queensland Law Society can point you to that process.",
  },
  {
    q: "Do you give a costs agreement?",
    a: "Yes. Every matter starts with a written costs agreement and disclosure as required under the Legal Profession Act 2007 (Qld), with a fixed professional fee stated upfront and disbursements listed at cost.",
  },
];

const legalServiceSchemaNode = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": `${CANONICAL}#legalservice`,
  name: FIRM.name,
  url: CANONICAL,
  telephone: FIRM.phone,
  email: FIRM.email,
  description: DESCRIPTION,
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  memberOf: {
    "@type": "Organization",
    name: "Queensland Law Society",
    url: "https://www.qls.com.au/",
  },
  knowsAbout: [
    "Conveyancing",
    "Property law",
    "Wills and estates",
    "Probate",
    "Elder law",
    "Business and commercial law",
  ],
  employee: PEOPLE.map((person) => ({
    "@type": "Person",
    name: person.name,
    jobTitle: person.role,
    image: person.photo.startsWith("http") ? person.photo : `https://coastsidelaw.com.au${person.photo}`,
    memberOf: { "@type": "Organization", name: "Queensland Law Society" },
    knowsAbout: person.areas,
    alumniOf: undefined,
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  about: { "@id": `${CANONICAL}#legalservice` },
  isPartOf: { "@id": `${SITE}/#website` },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE },
    { "@type": "ListItem", position: 2, name: "Queensland Law Society membership", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/queensland-law-society")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(legalServiceSchemaNode) },
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: QlsPage,
});

function QlsPage() {
  return (
    <>
      <PageHero
        eyebrow="Professional credentials"
        title="Queensland Law Society members, practising from Golden Beach"
        lead={`${FIRM.name} is a solicitor-led Queensland law firm at ${FIRM_ADDRESS}. Our lawyers are members of the Queensland Law Society and admitted solicitors of the Supreme Court of Queensland.`}
      />

      <section className="mx-auto max-w-5xl px-5 pt-12">
        <div className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-xl text-foreground">In short</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{ANSWER}</p>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground/80">
            Also searched as: {VARIANTS.join(", ")}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-2xl text-foreground">Firm and membership details</h2>
        <dl className="mt-6 divide-y divide-border border-y border-border">
          {FACTS.map((fact) => (
            <div key={fact.label} className="grid gap-1 py-4 sm:grid-cols-[220px_1fr] sm:gap-6">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{fact.label}</dt>
              <dd className="text-sm leading-relaxed text-foreground">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-2xl text-foreground">Our solicitors</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {PEOPLE.map((person) => (
              <div key={person.slug} className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-serif text-lg text-foreground">{person.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{person.role}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Admitted {person.admitted}. Admitted to the {person.courts.join(" and the ")}. Member of the
                  Queensland Law Society. Practises in {person.areas.join(", ").toLowerCase()}. Based in {person.based}.
                </p>
                <Link
                  to="/our-people"
                  className="mt-4 inline-block text-sm font-medium text-primary underline underline-offset-4"
                >
                  Full profile
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-2xl text-foreground">Practice areas</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {PRACTICE_AREAS.map((area) => (
            <Link
              key={area.to}
              to={area.to}
              className="rounded-sm border border-border bg-card p-6 transition hover:border-primary"
            >
              <h3 className="font-serif text-lg text-foreground">{area.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{area.blurb}</p>
            </Link>
          ))}
        </div>
        <Prose>
          <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
            Every matter is run by an admitted solicitor under a written costs agreement, with a fixed professional
            fee agreed before the work starts. See our{" "}
            <Link to="/fees" className="text-primary underline underline-offset-4">
              fixed fees
            </Link>
            , the{" "}
            <Link to="/wills-and-probate-golden-beach" className="text-primary underline underline-offset-4">
              Golden Beach wills and probate page
            </Link>{" "}
            or the{" "}
            <Link to="/scarborough-office" className="text-primary underline underline-offset-4">
              Scarborough office page
            </Link>
            .
          </p>
        </Prose>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-5 py-14">
          <h2 className="font-serif text-2xl text-foreground">Questions about our credentials</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <EnquiryForm heading="Ask a Queensland solicitor" suburb={FIRM.suburb} />
        <p className="mt-6 text-sm text-muted-foreground">
          Or call {CONTACT_NUMBERS.map((c) => `${c.name} on ${c.phone}`).join(" or ")}.
        </p>
      </section>
    </>
  );
}
